import { Card, CardContent, CardHeader, Box, Typography } from "@mui/material";
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { formatValue, pickPointColor, type BaseChartConfig, type Widget } from "./types";

const TOOLTIP_BG = "rgba(15, 23, 42, 0.92)";
const TOOLTIP_TEXT = "#f9fafb";

type Point = { label: string; value: number };

export function DonutCard({ widget }: { widget: Widget }) {
  const cfg = widget.config as BaseChartConfig;
  const rawPoints = widget.data?.points ?? [];
  const points: Point[] = rawPoints
    .filter((p) => p && typeof p === "object")
    .map((p) => {
      const o = p as Record<string, unknown>;
      return {
        label: String(o.label ?? o.name ?? o.x ?? ""),
        value:
          typeof o.value === "number" ? o.value
            : typeof o.y === "number" ? o.y
            : Number(o.value ?? o.y ?? 0),
      };
    });
  const fmt = cfg.format ?? "number";
  const total = points.reduce((a, p) => a + p.value, 0);

  return (
    <Card variant="outlined">
      {widget.title && (
        <CardHeader
          title={widget.title}
          titleTypographyProps={{ variant: "subtitle1", fontWeight: 700, letterSpacing: "-0.01em" }}
          subheaderTypographyProps={{ variant: "caption", color: "text.secondary" }}
          sx={{ pb: 1 }}
        />
      )}
      <CardContent sx={{ pt: 0 }}>
        {points.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {widget.data?.error ? `No data (${widget.data.error})` : "No data."}
            </Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={360}>
            <PieChart>
              <Pie
                data={points}
                dataKey="value"
                nameKey="label"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
              >
                {points.map((_, i) => (
                  <Cell key={i} fill={pickPointColor(i, points.length, cfg)} />
                ))}
              </Pie>
              <Legend
                verticalAlign="bottom"
                iconType="circle"
                wrapperStyle={{ fontSize: 12 }}
                formatter={(value, _entry, i) => {
                  const p = points[i as number];
                  const pct = total ? ((p.value / total) * 100).toFixed(1) : "0";
                  return `${value} · ${pct}%`;
                }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: TOOLTIP_BG, border: "none",
                  borderRadius: 6, color: TOOLTIP_TEXT,
                }}
                labelStyle={{ color: TOOLTIP_TEXT, fontWeight: 600 }}
                itemStyle={{ color: TOOLTIP_TEXT }}
                formatter={(v: number) => [formatValue(v, fmt), ""]}
              />
            </PieChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
